import type { Adjacency, RegionGrowResult } from './types';

/**
 * Close small holes left by spike rejection. After `growPlanarRegion`, a spike
 * (or surface pothole) shows up as a little island of rejected triangles fully
 * enclosed by the accepted region. Those were correctly rejected on geometry but
 * should be re-included so the highlight/capture region is solid.
 *
 * For each rejected component reachable from the region frontier we flood-fill
 * over rejected triangles; we absorb it iff it is fully enclosed (never touches a
 * mesh boundary edge) AND no larger than `maxHoleTriangles`. Absorbed triangles
 * get a BFS depth flowing inward from the surrounding accepted depths, so the
 * reveal wave passes smoothly over them.
 */
export function closeRegionHoles(
  result: RegionGrowResult,
  adj: Adjacency,
  triangleCount: number,
  opts: { maxHoleTriangles?: number } = {},
): RegionGrowResult {
  const budget = opts.maxHoleTriangles ?? 64;

  const accepted = new Uint8Array(triangleCount);
  const depthOf = new Int32Array(triangleCount).fill(-1);
  for (let i = 0; i < result.triangles.length; i++) {
    accepted[result.triangles[i]] = 1;
    depthOf[result.triangles[i]] = result.depths[i];
  }

  const rejVisited = new Uint8Array(triangleCount);
  const addTris: number[] = [];
  const addDepths: number[] = [];

  for (let i = 0; i < result.triangles.length; i++) {
    const t = result.triangles[i];
    for (let s = 0; s < 3; s++) {
      const seed = adj.adjacent[t * 3 + s];
      if (seed < 0 || accepted[seed] || rejVisited[seed]) continue;

      // Flood-fill the rejected component containing `seed`.
      const comp: number[] = [];
      let escaped = false;
      const stack = [seed];
      rejVisited[seed] = 1;
      while (stack.length) {
        const r = stack.pop() as number;
        comp.push(r);
        for (let k = 0; k < 3; k++) {
          const rn = adj.adjacent[r * 3 + k];
          if (rn < 0) {
            escaped = true; // touches a mesh boundary → open region, not a hole
            continue;
          }
          if (accepted[rn]) continue; // bordering the accepted region is expected
          if (!rejVisited[rn]) {
            rejVisited[rn] = 1;
            stack.push(rn);
          }
        }
      }
      if (escaped || comp.length > budget) continue;

      // Enclosed spike → absorb. Layer depths inward from accepted borders.
      const compSet = new Set(comp);
      const depth = new Map<number, number>();
      let frontier: number[] = [];
      for (const r of comp) {
        let best = -1;
        for (let k = 0; k < 3; k++) {
          const rn = adj.adjacent[r * 3 + k];
          if (rn >= 0 && accepted[rn]) best = Math.max(best, depthOf[rn]);
        }
        if (best >= 0) {
          depth.set(r, best + 1);
          frontier.push(r);
        }
      }
      while (frontier.length) {
        const next: number[] = [];
        for (const r of frontier) {
          const dr = depth.get(r) as number;
          for (let k = 0; k < 3; k++) {
            const rn = adj.adjacent[r * 3 + k];
            if (rn >= 0 && compSet.has(rn) && !depth.has(rn)) {
              depth.set(rn, dr + 1);
              next.push(rn);
            }
          }
        }
        frontier = next;
      }
      for (const r of comp) {
        addTris.push(r);
        addDepths.push(depth.get(r) ?? 0);
      }
    }
  }

  if (addTris.length === 0) return result;

  const triangles = new Uint32Array(result.triangles.length + addTris.length);
  const depths = new Uint32Array(triangles.length);
  triangles.set(result.triangles, 0);
  depths.set(result.depths, 0);
  triangles.set(addTris, result.triangles.length);
  depths.set(addDepths, result.depths.length);

  let maxDepth = result.maxDepth;
  for (const d of addDepths) if (d > maxDepth) maxDepth = d;

  return { ...result, triangles, depths, maxDepth };
}
